import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GameV1 } from '@/api/games';
import { Button } from '@/components/buttons';
import { GameItem, GameSizeConfig } from '@/components/GameItem';
import { GameItemPlug } from '@/components/GamesGrid/components/GameItemPlug';
import { GameItemMode, MediaType } from '@/helpers/enums';
import { useResponsive } from '@/modules/client';
import { useCommonStore } from '@/store';

import {
  ScLobbyGamesSlide,
  ScShowAllGamesCard,
  ScShowAllGamesCardProps,
} from './styled';
import { ColorMode, GameItemSize } from '@/components/GameItem/styled';
import { ScGameItemPlugProps } from '@/components/GamesGrid/styled';

export type PlugType = Record<never, never>;

export interface LobbyGamesSlideProps
  extends Pick<ScGameItemPlugProps, 'plugBlack'> {
  games?: GameV1[];
  link?: string;
  rows: number;
  cols: number;
  gameItemMode?: GameItemMode;
  blackShowAllCard?: boolean;
  gameColorMode?: ColorMode;
  showAllCardSizeConfig?: GameSizeConfig;
  plugs: PlugType[];
}

interface ShowAllCardProps extends ScShowAllGamesCardProps {
  to: string;
  showAllCardSizeConfig?: GameSizeConfig;
}

const gameSizesConfig: GameSizeConfig = {
  desktopL: GameItemSize.M,
  desktop: GameItemSize.S,
  tablet: GameItemSize.M,
  mobile: GameItemSize.M,
  mobileS: GameItemSize.S,
};

export const Slide: FC<LobbyGamesSlideProps> = ({
  games,
  link,
  rows,
  cols,
  gameItemMode,
  plugs,
  plugBlack,
  blackShowAllCard,
  gameColorMode,
  showAllCardSizeConfig,
}) => {
  const slideGames = useMemo(
    () => (link ? games?.slice(0, games.length - 1) : games),
    [games, link]
  );

  return (
    <ScLobbyGamesSlide rows={rows} cols={cols}>
      {slideGames?.map((game) => (
        <GameItem
          {...game}
          key={game.id}
          gameSizesConfig={gameSizesConfig}
          mode={gameItemMode}
          colorMode={gameColorMode}
        />
      ))}
      {plugs.map((_, idx) => (
        <GameItemPlug key={idx} plugBlack={plugBlack} plugGray={!plugBlack} />
      ))}
      {link && (
        <ShowAllCard
          to={link}
          black={blackShowAllCard}
          showAllCardSizeConfig={showAllCardSizeConfig}
        />
      )}
    </ScLobbyGamesSlide>
  );
};

const ShowAllCard: FC<ShowAllCardProps> = ({
  to,
  showAllCardSizeConfig = gameSizesConfig,
  ...props
}) => {
  const { t } = useTranslation('common');
  const { currentMediaType } = useResponsive();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const showAllSize = useMemo(() => {
    switch (currentMediaType) {
      case MediaType.DESKTOP_L:
        return showAllCardSizeConfig?.desktopL;
      case MediaType.DESKTOP:
        return showAllCardSizeConfig?.desktop;
      case MediaType.TABLET:
        return showAllCardSizeConfig?.tablet;
      case MediaType.MOBILE:
        return showAllCardSizeConfig?.mobile;
      case MediaType.MOBILE_S:
      default:
        return showAllCardSizeConfig?.mobileS;
    }
  }, [currentMediaType, showAllCardSizeConfig]);

  return (
    <ScShowAllGamesCard {...props} size={showAllSize}>
      <Button small to={to} wide autoPaddingHorizontal={isMobileDevice}>
        {t('button.showAll')}
      </Button>
    </ScShowAllGamesCard>
  );
};
